import { ChildSettlementsIcon } from 'assets';
import { childrenTexts } from 'consts/text';
import { ChildId, ChildSettlementDetails } from 'types';
import { parseStringIntoFormattedStringDate } from 'utils/parsers/dateTime/parseStringIntoFormattedStringDate';
import { parseStringIntoFormattedStringTime } from 'utils/parsers/dateTime/parseStringIntoFormattedStringTime';
import { DetailsColumn, DetailsRow, DetailsFrameBase, AppText } from 'components/atoms';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import parentHTTPService from 'services/HTTPService/parent/ParentHTTPService';

export type SettlementsProps = Pick<
    ChildSettlementDetails,
    | 'stayStart'
    | 'stayEnd'
    | 'periodStart'
    | 'periodEnd'
    | 'invoiceNotes'
    | 'invoiceRecipientId'
    | 'childId' // <-- 'childId' comes from the spread!
> & {
    parents?: { id: string; firstName: string; lastName: string }[] | null;
    childFullName?: string;
};

export const Settlements = ({
    stayStart,
    stayEnd,
    periodStart,
    periodEnd,
    invoiceNotes,
    invoiceRecipientId,
    childId,
    parents,
    childFullName,
}: SettlementsProps) => {
    const periodStartParsed = periodStart
        ? parseStringIntoFormattedStringDate(periodStart)
        : undefined;
    const periodEndParsed = periodEnd ? parseStringIntoFormattedStringDate(periodEnd) : undefined;
    const stayStartParsed = stayStart ? parseStringIntoFormattedStringTime(stayStart) : undefined;
    const stayEndParsed = stayEnd ? parseStringIntoFormattedStringTime(stayEnd) : undefined;

    const { data: parentProfileData } = useAppQuery(
        'PARENT_PROFILE',
        [childId],
        () => parentHTTPService.children.parentProfile.getParentProfile(childId as ChildId),
        {
            enabled: !!invoiceRecipientId && !!childId && (!parents || parents.length === 0),
            refetchOnWindowFocus: false,
        },
    );

    let recipientName = 'Domyślny (Pierwszy rodzic)';

    if (invoiceRecipientId) {
        if (invoiceRecipientId === childId) {
            recipientName = childFullName ? `Dziecko: ${childFullName}` : 'Dziecko';
        } else if (parents && parents.length > 0) {
            const invoiceRecipient = parents.find((p) => p.id === invoiceRecipientId);
            if (invoiceRecipient) {
                recipientName = `${invoiceRecipient.firstName} ${invoiceRecipient.lastName}`;
            }
        } else if (parentProfileData) {
            if (parentProfileData.id === invoiceRecipientId) {
                recipientName = `${parentProfileData.firstName} ${parentProfileData.lastName}`;
            } else {
                recipientName = 'Drugi rodzic';
            }
        }
    }

    return (
        <DetailsFrameBase
            icon={<ChildSettlementsIcon />}
            title={childrenTexts.childDetails.settlementData.sectionTitle}
        >
            <DetailsRow>
                <DetailsColumn
                    title={childrenTexts.childDetails.settlementData.settlementPeriodStart}
                    span={8}
                >
                    {periodStartParsed}
                </DetailsColumn>
                <DetailsColumn
                    title={childrenTexts.childDetails.settlementData.settlementPeriodEnd}
                    span={8}
                >
                    {periodEndParsed}
                </DetailsColumn>
                <DetailsColumn
                    title={childrenTexts.childDetails.settlementData.declaredStayHours}
                    span={8}
                >
                    {`${stayStartParsed}-${stayEndParsed}`}
                </DetailsColumn>
            </DetailsRow>

            <DetailsRow>
                <DetailsColumn title="Odbiorca faktury" span={24}>
                    {recipientName}
                </DetailsColumn>
            </DetailsRow>

            {invoiceNotes && (
                <DetailsRow>
                    <DetailsColumn title={childrenTexts.childDetails.settlementData.notes}>
                        <AppText textType="BodyMedium" margin={0}>
                            {invoiceNotes}
                        </AppText>
                    </DetailsColumn>
                </DetailsRow>
            )}
        </DetailsFrameBase>
    );
};
